import { FileText } from 'lucide-react'
import { FileDropzone } from '@/shared/components/FileDropzone'
import { LoadingSpinner } from '@/shared/components/ui/LoadingSpinner'
import { formatFileSize } from '@/shared/lib/validation'
import { useSignatureVerification } from '../hooks/useSignatureVerification'
import { ResultPanel } from './ResultPanel'

export function SignatureVerifier() {
  const {
    file,
    localHash,
    isHashing,
    isVerifying,
    result,
    error,
    handleFileSelect,
    verify,
    reset,
  } = useSignatureVerification()

  // Show result if verification is complete
  if (result) {
    return <ResultPanel result={result} onReset={reset} />
  }

  return (
    <div className="w-full max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center space-y-3">
        <h1 className="text-3xl md:text-4xl font-bold text-legal-900 dark:text-white">
          Verificador de Firmas Digitales
        </h1>
        <p className="text-lg text-legal-600 dark:text-legal-300">
          Validación técnica y legal de firmas en documentos PDF según Ley
          25.506 (Argentina)
        </p>
      </div>

      {/* File Upload */}
      {!file && (
        <FileDropzone
          onFileSelect={handleFileSelect}
          disabled={isHashing}
        />
      )}

      {/* File Info + Hash */}
      {file && (
        <div className="card">
          <div className="flex items-start gap-4">
            <FileText className="w-12 h-12 text-primary-600 dark:text-primary-400 flex-shrink-0" />

            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-legal-900 dark:text-white truncate">
                {file.name}
              </h3>
              <p className="text-sm text-legal-500 dark:text-legal-400">
                {formatFileSize(file.size)} • {file.type}
              </p>

              {/* Hash Display */}
              {isHashing && (
                <div className="mt-4 flex items-center gap-3">
                  <LoadingSpinner size="sm" />
                  <p className="text-sm text-legal-600 dark:text-legal-300">
                    Calculando hash SHA-256 localmente...
                  </p>
                </div>
              )}

              {localHash && !isHashing && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-legal-700 dark:text-legal-300">
                    Hash SHA-256 (calculado localmente):
                  </p>
                  <code className="block text-xs font-mono bg-legal-100 dark:bg-legal-700 p-3 rounded break-all text-legal-900 dark:text-legal-100">
                    {localHash}
                  </code>
                  <p className="text-xs text-legal-500 dark:text-legal-400">
                    ✓ Hash generado en tu navegador sin subir el archivo
                  </p>
                </div>
              )}
            </div>

            <button
              onClick={reset}
              className="text-legal-500 dark:text-legal-400 hover:text-legal-700 dark:hover:text-legal-200 text-sm"
              disabled={isVerifying}
            >
              Cambiar
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="card border-2 border-red-500 dark:border-red-600 bg-red-50 dark:bg-red-900/20">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-500 dark:bg-red-600 flex items-center justify-center">
              <span className="text-white text-sm font-bold">!</span>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-red-900 dark:text-red-200 mb-1">Error</h3>
              <p className="text-red-800 dark:text-red-300">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Verify Button */}
      {file && localHash && !isHashing && (
        <button
          onClick={verify}
          disabled={isVerifying}
          className="btn-primary w-full py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isVerifying ? (
            <div className="flex items-center justify-center gap-3">
              <LoadingSpinner size="sm" className="border-white border-t-transparent" />
              <span>Analizando Criptografía...</span>
            </div>
          ) : (
            'Verificar Firma Digital'
          )}
        </button>
      )}

      {/* Info Box */}
      <div className="card bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-700">
        <h3 className="font-semibold text-primary-900 dark:text-primary-200 mb-2">
          ¿Qué verificamos?
        </h3>
        <ul className="space-y-2 text-sm text-primary-800 dark:text-primary-300">
          <li className="flex items-start gap-2">
            <span className="text-primary-600 dark:text-primary-400">•</span>
            <span>
              <strong>Firma Digital PKI:</strong> Validez criptográfica según
              Ley 25.506
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary-600 dark:text-primary-400">•</span>
            <span>
              <strong>Certificado:</strong> Verificación OCSP/CRL, vigencia,
              emisor
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary-600 dark:text-primary-400">•</span>
            <span>
              <strong>Integridad:</strong> Comparación de hash local vs servidor
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary-600 dark:text-primary-400">•</span>
            <span>
              <strong>Constancia:</strong> Generación de PDF probatorio
            </span>
          </li>
        </ul>
      </div>
    </div>
  )
}
